import { gql } from '@urql/core';
import { cacheExchange, Client, CombinedError, fetchExchange } from 'urql';

import { ufoEventGetJiraSitesFromAgg } from '@/components/signup/utils/ufo-events';
import { GATEWAY_URL } from '@/libraries/growth-unified-profile-client/constants.ts';
import { jiraResponseSchema } from '@/libraries/logged-in-user-jira-sites-client/all-jira-sites.schema.ts';
import { LoggedInUserJiraSitesClientOptions } from '@/libraries/logged-in-user-jira-sites-client/types.ts';

export class LoggedInUserJiraSitesClient {
	private readonly client: Client;
	public constructor(options?: LoggedInUserJiraSitesClientOptions) {
		const fetchOptions = options?.slauthToken
			? {
					headers: {
						Authorization: `SLAUTH ${options.slauthToken || ''}`,
					},
				}
			: {
					credentials: 'include' as RequestCredentials,
				};

		this.client = new Client({
			url: GATEWAY_URL,
			exchanges: [cacheExchange, fetchExchange],
			fetchOptions,
		});
	}

	public getJiraSites = async (cloudId: string) => {
		ufoEventGetJiraSitesFromAgg.start();
		if (!cloudId) {
			ufoEventGetJiraSitesFromAgg.failure({
				error: 'No Cloud ID provided',
			});
			return undefined;
		}

		const GET_JIRA_SITES_QUERY = gql(`
                query getAllJiraProjects {
                    jira {
                        allJiraProjects(
                            cloudId: "${cloudId}"
                            filter: {types: [SOFTWARE], sortBy: {order: DESC, sortBy: LAST_ISSUE_UPDATED_TIME}}
                            first: 10
                        ) {
                            pageInfo {
                                hasNextPage
                            }
                            edges {
                                node {
                                    key
                                    name
                                    projectUrl
                                    webUrl
                                    assignableUsers(first: 5) {
                                        totalCount
                                        edges {
                                            node {
                                                name
                                                picture
                                                accountStatus
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            `);

		try {
			const {
				data,
				error,
			}: {
				data?: unknown;
				error?: CombinedError | undefined;
			} = await this.client.query(GET_JIRA_SITES_QUERY, { cloudId });
			if (error?.message) {
				ufoEventGetJiraSitesFromAgg.failure({
					error: error,
				});
				return undefined;
			}

			try {
				const allJiraSites = jiraResponseSchema.parse(data);
				ufoEventGetJiraSitesFromAgg.success();
				return allJiraSites;
			} catch (e) {
				ufoEventGetJiraSitesFromAgg.failure({
					error: e,
				});
				return undefined;
			}
		} catch (e) {
			ufoEventGetJiraSitesFromAgg.failure({
				error: e,
			});
			return undefined;
		}
	};
}
